import { ApiEndpoint, FormType } from '@enums';
import { EndpointType } from '@types';
import type { StringRecord } from '@types';

/**
 * Configuration for initializing nested state structure
 */
interface StateInitializerConfig {
  /** The state object to modify */
  state: StringRecord;
  /** Form type identifier */
  formType: FormType;
  /** Endpoint identifier */
  endpoint: EndpointType;
  /** Properties to initialize with their default values */
  properties: StringRecord;
}

/**
 * Initializes nested state structure ensuring all required paths exist
 *
 * Safely creates a three-level nested structure: state[formType][property][endpoint]
 * Each property is initialized with its corresponding default value if it doesn't exist
 *
 * @param config - Configuration object containing state, identifiers, and schema
 *
 * @example
 * ```typescript
 * initializeStateStructure({
 *   state: formState,
 *   formType: FormType.Cdn,
 *   endpoint: ApiEndpoint.DEFAULT,
 *   properties: {
 *     selectedIds: [],
 *     formData: {},
 *     validationErrors: null
 *   }
 * });
 * ```
 */
export const initializeStateStructure = ({
  state,
  formType,
  endpoint = ApiEndpoint.DEFAULT,
  properties,
}: StateInitializerConfig): void => {
  if (!state[formType]) {
    state[formType] = {};
  }

  const formTypeRef = state[formType] as StringRecord;

  Object.entries(properties).forEach(([propertyKey, defaultValue]) => {
    if (!formTypeRef[propertyKey]) {
      formTypeRef[propertyKey] = {};
    }

    const propertyRecord = formTypeRef[propertyKey] as StringRecord;
    if (propertyRecord[endpoint] === undefined) {
      propertyRecord[endpoint] = defaultValue;
    }
  });
};
